import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Mountain, Search, MapPin, Trees, Landmark, Waves, Church,
  Utensils, Binoculars, TreePine, ChevronRight, Eye, Navigation,
  Compass, Car, ArrowRight, Star, Clock
} from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import Layout from "@/components/layout/Layout";
import { AdBanner } from "@/components/shared/AdPlaceholder";
import axios from "axios";
import { API } from "@/App";

const getImageUrl = (url) => {
  if (!url) return null;
  if (url.startsWith('http')) return url;
  const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
  return url.startsWith('/uploads/') ? `${backendUrl}/api${url}` : `${backendUrl}${url}`;
};

const categoryIcons = {
  natural: Trees,
  historical: Landmark,
  beach: Waves,
  adventure: Mountain,
  cultural: Landmark,
  religious: Church,
  gastronomy: Utensils,
  viewpoint: Binoculars,
  park: TreePine,
  other: MapPin,
};

const categoryGradients = {
  natural: "from-emerald-600 to-teal-700",
  historical: "from-amber-600 to-yellow-700",
  beach: "from-sky-500 to-blue-600",
  adventure: "from-orange-500 to-red-600",
  cultural: "from-violet-600 to-purple-700",
  religious: "from-rose-500 to-pink-600",
  gastronomy: "from-red-500 to-orange-600",
  viewpoint: "from-cyan-500 to-teal-600",
  park: "from-green-600 to-emerald-700",
  other: "from-slate-500 to-gray-600",
};

const categoryBg = {
  natural: "bg-emerald-50 text-emerald-700 border-emerald-200",
  historical: "bg-amber-50 text-amber-700 border-amber-200",
  beach: "bg-sky-50 text-sky-700 border-sky-200",
  adventure: "bg-orange-50 text-orange-700 border-orange-200",
  cultural: "bg-violet-50 text-violet-700 border-violet-200",
  religious: "bg-rose-50 text-rose-700 border-rose-200",
  gastronomy: "bg-red-50 text-red-700 border-red-200",
  viewpoint: "bg-cyan-50 text-cyan-700 border-cyan-200",
  park: "bg-green-50 text-green-700 border-green-200",
  other: "bg-gray-50 text-gray-700 border-gray-200",
};

// Parse distances text into structured array
function parseDistances(distancesText) {
  if (!distancesText) return [];
  return distancesText.split('\n').filter(Boolean).map(line => {
    const match = line.match(/(?:desde\s+)?(.+?):\s*(.+)/i);
    if (match) return { city: match[1].replace(/^desde\s+/i, '').trim(), time: match[2].trim() };
    return { city: line.trim(), time: '' };
  }).filter(d => d.city);
}

function DestinationCard({ destination, featured = false }) {
  const IconComponent = categoryIcons[destination.category] || MapPin;
  const gradient = categoryGradients[destination.category] || categoryGradients.other;
  const catBg = categoryBg[destination.category] || categoryBg.other;
  const image = getImageUrl(destination.featured_image_url);
  const distances = parseDistances(destination.distances);
  const firstDistance = distances[0];

  if (featured) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <Link
          to={`/destinos/${destination.slug}`}
          className="block group"
          data-testid={`destination-featured-${destination.destination_id}`}
        >
          <div className="relative rounded-3xl overflow-hidden shadow-lg hover:shadow-2xl transition-shadow duration-500">
            <div className="grid grid-cols-1 lg:grid-cols-2">
              {/* Image side */}
              <div className="relative aspect-[4/3] lg:aspect-auto lg:min-h-[400px] overflow-hidden">
                {image ? (
                  <img
                    src={image}
                    alt={destination.name}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                  />
                ) : (
                  <div className={`w-full h-full bg-gradient-to-br ${gradient} flex items-center justify-center`}>
                    <Mountain className="h-24 w-24 text-white/30" />
                  </div>
                )}
                <div className="absolute inset-0 bg-gradient-to-r from-black/40 via-transparent to-transparent lg:bg-gradient-to-t lg:from-black/50 lg:via-transparent lg:to-transparent" />
                <div className="absolute top-5 left-5">
                  <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-amber-400 text-amber-950 text-xs font-bold uppercase tracking-wider">
                    <Star className="h-3 w-3" />
                    Destacado
                  </span>
                </div>
                {destination.view_count > 0 && (
                  <div className="absolute top-5 right-5 bg-black/50 backdrop-blur-md text-white text-xs px-3 py-1.5 rounded-full flex items-center gap-1.5">
                    <Eye className="h-3.5 w-3.5" />
                    {destination.view_count}
                  </div>
                )}
              </div>

              {/* Content side */}
              <div className="bg-white p-8 lg:p-10 flex flex-col justify-between">
                <div>
                  <Badge className={`${catBg} border mb-4`}>
                    <IconComponent className="h-3.5 w-3.5 mr-1.5" />
                    {destination.category_name}
                  </Badge>
                  <h2 className="font-heading text-3xl lg:text-4xl font-bold text-gray-900 mb-3 group-hover:text-primary transition-colors">
                    {destination.name}
                  </h2>
                  {destination.municipio && (
                    <p className="flex items-center gap-2 text-gray-500 mb-4">
                      <MapPin className="h-4 w-4 text-primary" />
                      {destination.municipio}{destination.departamento ? `, ${destination.departamento}` : ''}
                    </p>
                  )}
                  {destination.short_description && (
                    <p className="text-gray-600 leading-relaxed mb-6 line-clamp-3">
                      {destination.short_description}
                    </p>
                  )}
                </div>

                {/* Distances highlight */}
                {distances.length > 0 && (
                  <div className="mb-6">
                    <div className="flex items-center gap-2 mb-3">
                      <Car className="h-4 w-4 text-amber-600" />
                      <span className="text-sm font-semibold text-gray-700">Distancias</span>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {distances.slice(0, 3).map((d, i) => (
                        <span key={i} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-amber-50 border border-amber-100 text-sm">
                          <Navigation className="h-3 w-3 text-amber-600" />
                          <span className="text-gray-500">{d.city}:</span>
                          <span className="font-semibold text-amber-700">{d.time}</span>
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                <div className="flex items-center gap-2 text-primary font-semibold group-hover:gap-3 transition-all">
                  Descubrir este lugar
                  <ArrowRight className="h-5 w-5" />
                </div>
              </div>
            </div>
          </div>
        </Link>
      </motion.div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-50px" }}
      transition={{ duration: 0.4 }}
    >
      <Link
        to={`/destinos/${destination.slug}`}
        className="block group h-full"
        data-testid={`destination-card-${destination.destination_id}`}
      >
        <div className="bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-xl transition-all duration-400 h-full flex flex-col border border-gray-100">
          {/* Image */}
          <div className="relative aspect-[16/10] overflow-hidden">
            {image ? (
              <img
                src={image}
                alt={destination.name}
                className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
              />
            ) : (
              <div className={`w-full h-full bg-gradient-to-br ${gradient} flex items-center justify-center`}>
                <IconComponent className="h-14 w-14 text-white/40" />
              </div>
            )}
            <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />

            {/* Category */}
            <div className="absolute top-3 left-3">
              <Badge className={`${catBg} border text-xs`}>
                <IconComponent className="h-3 w-3 mr-1" />
                {destination.category_name}
              </Badge>
            </div>

            {/* Views */}
            {destination.view_count > 0 && (
              <div className="absolute top-3 right-3 bg-black/50 backdrop-blur-md text-white text-xs px-2.5 py-1 rounded-full flex items-center gap-1">
                <Eye className="h-3 w-3" />
                {destination.view_count}
              </div>
            )}

            {/* Title & Location overlay */}
            <div className="absolute bottom-0 left-0 right-0 p-4">
              <h3 className="text-lg font-bold text-white line-clamp-2 group-hover:underline decoration-2 underline-offset-2">
                {destination.name}
              </h3>
              {destination.municipio && (
                <p className="text-white/80 text-sm flex items-center gap-1 mt-1">
                  <MapPin className="h-3 w-3 flex-shrink-0" />
                  {destination.municipio}
                </p>
              )}
            </div>
          </div>

          {/* Content */}
          <div className="p-4 flex-1 flex flex-col">
            {destination.short_description && (
              <p className="text-gray-500 text-sm line-clamp-2 mb-3">
                {destination.short_description}
              </p>
            )}

            {/* Distance chip - prominent */}
            {firstDistance && (
              <div className="mb-3 mt-auto">
                <div className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-slate-800 text-white text-xs font-medium">
                  <Car className="h-3 w-3 text-amber-400" />
                  {firstDistance.city}: {firstDistance.time}
                  {distances.length > 1 && (
                    <span className="ml-1 text-gray-400">+{distances.length - 1}</span>
                  )}
                </div>
              </div>
            )}

            {/* Tags + CTA */}
            <div className="flex items-center justify-between mt-auto pt-3 border-t border-gray-100">
              {destination.tags && destination.tags.length > 0 ? (
                <div className="flex flex-wrap gap-1">
                  {destination.tags.slice(0, 2).map((tag, i) => (
                    <span key={i} className="text-xs text-gray-400 bg-gray-50 px-2 py-0.5 rounded-full">
                      #{tag}
                    </span>
                  ))}
                </div>
              ) : <div />}
              <span className="text-primary text-sm font-semibold flex items-center gap-1 group-hover:gap-2 transition-all whitespace-nowrap">
                Explorar
                <ChevronRight className="h-4 w-4" />
              </span>
            </div>
          </div>
        </div>
      </Link>
    </motion.div>
  );
}

export default function DestinosPage() {
  const [destinations, setDestinations] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("");
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const limit = 12;

  useEffect(() => {
    fetchDestinations();
    fetchCategories();
  }, [searchQuery, categoryFilter, page]);

  useEffect(() => {
    axios.post(`${API}/track-page-view?page=destinos`).catch(() => {});
  }, []);

  const fetchDestinations = async () => {
    try {
      const params = new URLSearchParams();
      params.append("skip", String(page * limit));
      params.append("limit", String(limit));
      if (searchQuery) params.append("search", searchQuery);
      if (categoryFilter) params.append("category", categoryFilter);

      const response = await axios.get(`${API}/public/destinos?${params}`);
      setDestinations(response.data.items || []);
      setTotal(response.data.total || 0);
    } catch (error) {
      console.error("Error fetching destinations:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API}/destination/categories`);
      setCategories(response.data.categories || []);
    } catch (error) {
      console.error("Error fetching categories:", error);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(0);
    fetchDestinations();
  };

  // First destination as featured (most viewed or first)
  const featured = destinations.length > 0 ? destinations[0] : null;
  const rest = destinations.slice(1);

  return (
    <Layout>
      {/* === HERO === */}
      <section className="relative overflow-hidden bg-gray-950 py-20 md:py-28" data-testid="destinos-hero">
        {/* Bg pattern */}
        <div className="absolute inset-0 opacity-[0.03]" style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
        }} />
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[400px] bg-gradient-to-b from-emerald-500/10 to-transparent rounded-full blur-3xl" />
        <div className="absolute bottom-0 right-0 w-[500px] h-[300px] bg-gradient-to-tl from-amber-500/10 to-transparent rounded-full blur-3xl" />

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center"
          >
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/5 border border-white/10 text-emerald-400 text-sm mb-6 backdrop-blur-sm">
              <Compass className="h-4 w-4" />
              Explora la regi&oacute;n sur de Honduras
            </div>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-5 leading-tight">
              Destinos<br />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-emerald-400 to-amber-400">
                Tur&iacute;sticos
              </span>
            </h1>
            <p className="text-gray-400 text-base sm:text-lg max-w-xl mx-auto mb-10">
              Cascadas, playas, miradores y tesoros escondidos. Encuentra tu pr&oacute;ximo destino.
            </p>

            {/* Search inline */}
            <form onSubmit={handleSearch} className="max-w-2xl mx-auto">
              <div className="relative flex items-center bg-white/5 border border-white/10 rounded-2xl backdrop-blur-sm p-1.5">
                <Search className="absolute left-5 h-5 w-5 text-gray-500" />
                <Input
                  placeholder="Buscar destinos, cascadas, playas..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-12 h-12 bg-transparent border-0 text-white placeholder:text-gray-500 focus-visible:ring-0 focus-visible:ring-offset-0"
                  data-testid="destination-search"
                />
                <Button type="submit" className="h-12 px-6 rounded-xl bg-emerald-600 hover:bg-emerald-500 text-white shrink-0">
                  Buscar
                </Button>
              </div>
            </form>
          </motion.div>

          {/* Stats */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3, duration: 0.5 }}
            className="flex justify-center gap-8 md:gap-16 mt-12"
          >
            {[
              { label: "Destinos", value: total || "0", icon: Mountain },
              { label: "Categor\u00edas", value: categories.length || "0", icon: Compass },
              { label: "Municipios", value: new Set(destinations.map(d => d.municipio).filter(Boolean)).size || "0", icon: MapPin },
            ].map((stat, i) => (
              <div key={i} className="text-center">
                <stat.icon className="h-5 w-5 text-emerald-400 mx-auto mb-2" />
                <p className="text-2xl md:text-3xl font-bold text-white">{stat.value}</p>
                <p className="text-xs text-gray-500 uppercase tracking-wider">{stat.label}</p>
              </div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* === CATEGORY PILLS === */}
      <section className="bg-white border-b sticky top-0 z-30" data-testid="destinos-categories">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex gap-2 py-4 overflow-x-auto scrollbar-hide -mx-4 px-4">
            <button
              onClick={() => { setCategoryFilter(""); setPage(0); }}
              className={`flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-medium whitespace-nowrap transition-all duration-200 border ${
                !categoryFilter
                  ? "bg-gray-900 text-white border-gray-900 shadow-md"
                  : "bg-white text-gray-600 border-gray-200 hover:border-gray-300 hover:bg-gray-50"
              }`}
              data-testid="category-all"
            >
              <Mountain className="h-4 w-4" />
              Todos
            </button>
            {categories.map(cat => {
              const IconComponent = categoryIcons[cat.id] || MapPin;
              const isActive = categoryFilter === cat.id;
              return (
                <button
                  key={cat.id}
                  onClick={() => { setCategoryFilter(cat.id); setPage(0); }}
                  className={`flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-medium whitespace-nowrap transition-all duration-200 border ${
                    isActive
                      ? "bg-gray-900 text-white border-gray-900 shadow-md"
                      : "bg-white text-gray-600 border-gray-200 hover:border-gray-300 hover:bg-gray-50"
                  }`}
                  data-testid={`category-${cat.id}`}
                >
                  <IconComponent className="h-4 w-4" />
                  {cat.name}
                </button>
              );
            })}
          </div>
        </div>
      </section>

      {/* === DESTINATIONS === */}
      <section className="py-10 md:py-14 bg-gray-50/50" data-testid="destinos-grid">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {loading ? (
            <div className="space-y-8">
              {/* Featured skeleton */}
              <div className="rounded-3xl overflow-hidden bg-white shadow-sm animate-pulse">
                <div className="grid grid-cols-1 lg:grid-cols-2">
                  <div className="aspect-[4/3] lg:min-h-[400px] bg-gray-200" />
                  <div className="p-10 space-y-4">
                    <div className="h-6 w-24 bg-gray-200 rounded-full" />
                    <div className="h-10 w-3/4 bg-gray-200 rounded" />
                    <div className="h-4 w-1/3 bg-gray-200 rounded" />
                    <div className="h-16 w-full bg-gray-200 rounded" />
                    <div className="flex gap-2">
                      <div className="h-8 w-40 bg-gray-200 rounded-lg" />
                      <div className="h-8 w-40 bg-gray-200 rounded-lg" />
                    </div>
                  </div>
                </div>
              </div>
              {/* Grid skeleton */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {[1, 2, 3].map(i => (
                  <div key={i} className="rounded-2xl overflow-hidden bg-white shadow-sm animate-pulse">
                    <div className="aspect-[16/10] bg-gray-200" />
                    <div className="p-4 space-y-3">
                      <div className="h-4 w-full bg-gray-200 rounded" />
                      <div className="h-3 w-2/3 bg-gray-200 rounded" />
                      <div className="h-8 w-40 bg-gray-200 rounded-lg" />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : destinations.length === 0 ? (
            <div className="text-center py-24">
              <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <Mountain className="h-10 w-10 text-gray-300" />
              </div>
              <h3 className="text-xl font-semibold text-gray-700 mb-2">
                No se encontraron destinos
              </h3>
              <p className="text-gray-500 max-w-md mx-auto">
                {searchQuery || categoryFilter
                  ? "Intenta con otros filtros de b\u00fasqueda"
                  : "Pronto agregaremos nuevos destinos tur\u00edsticos"}
              </p>
              {(searchQuery || categoryFilter) && (
                <Button
                  variant="outline"
                  onClick={() => { setSearchQuery(""); setCategoryFilter(""); setPage(0); }}
                  className="mt-6"
                >
                  Limpiar filtros
                </Button>
              )}
            </div>
          ) : (
            <div className="space-y-10">
              {/* Featured destination */}
              {page === 0 && featured && !categoryFilter && !searchQuery && (
                <DestinationCard destination={featured} featured />
              )}

              {/* Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {(page === 0 && !categoryFilter && !searchQuery ? rest : destinations).map((dest) => (
                  <DestinationCard key={dest.destination_id} destination={dest} />
                ))}
              </div>

              {/* Pagination */}
              {total > limit && (
                <div className="flex items-center justify-center gap-3 pt-4">
                  <Button
                    variant="outline"
                    onClick={() => setPage(p => Math.max(0, p - 1))}
                    disabled={page === 0}
                    className="rounded-xl"
                    data-testid="pagination-prev"
                  >
                    Anterior
                  </Button>
                  <div className="flex items-center gap-1">
                    {Array.from({ length: Math.ceil(total / limit) }, (_, i) => (
                      <button
                        key={i}
                        onClick={() => setPage(i)}
                        className={`w-10 h-10 rounded-xl text-sm font-medium transition-colors ${
                          page === i
                            ? "bg-gray-900 text-white"
                            : "text-gray-500 hover:bg-gray-100"
                        }`}
                      >
                        {i + 1}
                      </button>
                    ))}
                  </div>
                  <Button
                    variant="outline"
                    onClick={() => setPage(p => p + 1)}
                    disabled={(page + 1) * limit >= total}
                    className="rounded-xl"
                    data-testid="pagination-next"
                  >
                    Siguiente
                  </Button>
                </div>
              )}
            </div>
          )}
        </div>
      </section>

      {/* Ad Banner */}
      <section className="py-8 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <AdBanner />
        </div>
      </section>

      {/* === CTA === */}
      <section className="relative py-20 overflow-hidden bg-gray-950">
        <div className="absolute inset-0 opacity-[0.04]" style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='40' height='40' viewBox='0 0 40 40' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='%23ffffff'%3E%3Ccircle cx='20' cy='20' r='1.5'/%3E%3C/g%3E%3C/svg%3E")`,
        }} />
        <div className="absolute top-0 left-0 w-[400px] h-[300px] bg-emerald-500/10 rounded-full blur-3xl" />
        <div className="absolute bottom-0 right-0 w-[400px] h-[300px] bg-amber-500/10 rounded-full blur-3xl" />
        <div className="relative max-w-3xl mx-auto px-4 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <div className="w-14 h-14 bg-emerald-500/10 rounded-2xl flex items-center justify-center mx-auto mb-6">
              <MapPin className="h-7 w-7 text-emerald-400" />
            </div>
            <h2 className="font-heading text-3xl sm:text-4xl font-bold text-white mb-4">
              &iquest;Conoces un destino que debemos incluir?
            </h2>
            <p className="text-gray-400 mb-8 max-w-lg mx-auto">
              Ay&uacute;danos a descubrir y compartir todos los tesoros escondidos de nuestra regi&oacute;n
            </p>
            <Link to="/contacto">
              <Button
                size="lg"
                className="bg-emerald-600 hover:bg-emerald-500 text-white text-lg px-8 py-6 rounded-2xl shadow-lg shadow-emerald-900/30"
                data-testid="suggest-destination-btn"
              >
                Sugerir un destino
                <ArrowRight className="h-5 w-5 ml-2" />
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>
    </Layout>
  );
}
